import type { SelectedAccessory } from './accessoryTypes';
import type {
  CachedPrice,
  Car,
  CarId,
  IncentivesProgramOffer,
} from './carTypes';
import {
  ActionType,
  CreditRating,
  FinanceType,
  IncomeSubType,
  IncomeType,
  OnlineStep,
  Origin,
  PaycheckFrequency,
  SourceType,
  TestDriveDeliveryMethod,
  TradeInCondition,
  TradeInSource,
} from './constants';
import type { DealerFee, DealUpgrade, TaxBreakdown } from './dealerTypes';

import type { DMSDealInfo, DMSType } from './dmsTypes';
import type { FinanceResponse, FinanceResponseId } from './financingTypes';
import { VehicleOptionIdHash } from './kbbTypes';
import { PayoffRequestStatus } from './lossExpressTypes';
import type { FullOffer, Offer } from './paymentTypes';
import type { SelectedBundleId, SelectedProduct } from './penTypes';
import { TradeInMeta } from './tradeInTypes';
import { isDefined } from './utils/tsutils';
import { PayoffMeta } from './payoffTypes';
import { DMSAnalyticsData } from './dmsAnalyticsData';
import { SalesType } from './DmsSalesType';
import { OfferData } from './deskingTypes';

export interface CreditRange {
  low: number;
  high: number;
}

export enum RentType {
  rent = 'rent',
  own = 'own',
  family = 'family',
  mortgage = 'mortgage',
  other = 'other',
}

export enum MaritalStatus {
  single = 'Single',
  married = 'Married',
  divorced = 'Divorced',
  widowed = 'Widowed',
  separated = 'Separated',
}

export enum HasCoapplicant {
  yes = 'yes',
  no = 'no',
}

export enum CoapplicantRelation {
  residesWith = 'Resides With',
  parent = 'Parent',
  spouse = 'Spouse',
  other = 'Other',
}

export enum ReferenceType {
  friend = 'Friend (over 1 year)',
  relative = 'Nearest Living Relative',
}

export enum Result {
  SOLD = 'sold',
  DID_NOT_PURCHASE = 'did-not-purchase',
}

export interface DocumentScan {
  imageSrc: string;
  displayName: string;
}

// any atc cache with a version not equal to this will be ignored and rebuilt
export const ATC_CURRENT_CACHE_VERSION = 6;

export type ATCTaxesAndFees = {
  [index in 'purchase' | 'lease']?: ATCTaxesAndFeesEntry;
} & { error: boolean; version?: number };

export interface ATCTaxesAndFeesEntry {
  // tax is the overall sales tax for the vehicle
  tax: number;
  // tax breakdown is the breakdown of the sales tax into each component
  // ex. State tax, County Tax, etc...
  taxBreakdown: TaxBreakdown;
  fees: DealerFee[];
}

export interface PriceOverride {
  source: Origin;
  sourceName: string;
  carId: string;
  carPrice: number;
  cachedPrice?: CachedPrice;
  financeType?: string; // used for source display purposes only and doesn't affect whether the override gets used
  expiry?: number; // unix timestamp
  createdAt: number;
}

export type PriceOverrides = { [index in Origin]?: PriceOverride };

export interface Reference {
  type?: ReferenceType;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  zip?: string;
  city?: string;
  state?: string;
}

export interface Applicant {
  firstName?: string;
  lastName?: string;
  middleName?: string | null;

  socialSec?: string;
  socialInsuranceNumber?: string; // ssn equivalent for Canada
  birthday?: string;
  email?: string;
  phone?: string;
  homePhone?: string;

  preferredContact?: string;

  zip?: string; // postal code for Canadian deals
  address?: string;
  apt?: string;
  city?: string;
  county?: string;
  state?: string; // province for Canadian deals
  yearsAtAddress?: number;
  monthsAtAddress?: number;
  rentType?: RentType;
  mortgageLease?: number | null;

  employmentStatus?: string;
  isEmployed?: 'yes' | true;
  jobTitle?: string;
  jobId?: number;
  companyName?: string;
  companyStart?: string;
  monthlySalary?: number;
  employerPhone?: string;
  otherIncome?: number;
  otherIncomeSource?: string;
  otherIncomeSourceKey?: string;

  maritalStatus?: MaritalStatus;
  hasCoapplicant?: HasCoapplicant;
  coapplicantRelation?: CoapplicantRelation;

  driverLicenseNumber?: string;
  driverLicenseState?: string;

  companyAddress?: string;
  companyCity?: string;
  companyState?: string;
  companyZip?: string;

  prevZip?: string;
  prevAddress?: string;
  prevApt?: string;
  prevCity?: string;
  prevState?: string;
  prevYearsAtAddress?: number;
  prevMonthsAtAddress?: number;
  prevJobTitle?: string;
  prevCompanyName?: string;
  prevYearsAtCompany?: number;
  prevMonthsAtCompany?: number;
  prevCompanyPhone?: string;
  prevCompanyAddress?: string;
  prevCompanyCity?: string;
  prevCompanyState?: string;
  prevCompanyZip?: string;

  references?: Reference[] | null;
  comments?: string;

  yearsAtCompany?: number;
  monthsAtCompany?: number;
  spouse?: ApplicantSpouse;
  incomeData?: IncomeData | null;
}

export interface ApplicantSpouse {
  firstName?: string;
  lastName?: string;
  address?: string;
  apt?: string;
  state?: string;
  city?: string;
  zip?: string;
  county?: string;
  date?: string;
}

export interface IncomeData {
  incomeType?: IncomeType | null;
  incomeSubType?: IncomeSubType | null;
  annualIncome?: number | null;
  additionalAnnualIncome?: number | null;
  hourlyIncomeRate?: number | null;
  hoursPerWeek?: number | null;
  otherIncomeType?: string | null;
  otherIncomeDescription?: string | null;
  otherIncomeAnnualAmount?: number | null;
  paycheckFrequency?: PaycheckFrequency | null;
}
export interface TradeInRecord {
  id: string;
  data: TradeIn;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  dealId?: string;
  pencilId?: string;
}

export interface TradeIn {
  recordId?: string; // trade in table record id
  id?: string; // VIN
  uuid?: string; // unique identifier
  year?: { id?: number; name?: number | string | null };
  make?: { id?: number; name?: string | null };
  model?: { id?: number; name?: string | null };
  trim?: { id?: number; name?: string | null };
  style?: { id?: number; name?: string | null };
  series?: { id?: number; name?: string | null };
  tradeValue?: number;
  mileage?: string;
  payOff?: string | number;
  zip?: string;
  appraisalId?: string;
  pushedToCrm?: boolean;
  source?: TradeInSource | null;
  tradeACV?: number | null; // Mostly to comply with CRM pulls and subsequent push of tradeIns
  KBBVehicleId?: number; // For KBB Ico
  tradeInAllowance?: number | null;
  tradeInTaxCredit?: boolean;
  tradeInCondition?: TradeInCondition;
  condition?: string;
  amountOwed?: number | null;
  tradeInMeta?: TradeInMeta;
  uvc?: string; // For Blackbook Drilldown
  vehicleOptionIds?: string[]; // For KBB
  vehicleOptionIdsHash?: VehicleOptionIdHash; // For KBB
  // eslint-disable-next-line @typescript-eslint/ban-types
  addDeductCodesHash?: object; // For Blackbook
  currentLienHolder?: string;
  hasNonStandardOptions?: boolean;
  tradePendingVehicleId?: string; // For TradePending
  license?: string; // For dapi RIC endpoints
  payoffLookup?: boolean; // For where user is engaging Loss Express payoff lookup service
  payoffResponseId?: string; // For payoff id from PayoffResponses table
  payoffRequestStatus?: PayoffRequestStatus; // For payoff request status
  payoffExpiredDate?: string; // For payoff expired date (2020-08-22T00:00:00.000Z)
  payoffEstimated?: boolean;
  lenderName?: string;
  perDiem?: number;
  payoffMeta?: PayoffMeta;
  tradePayoffSentToCrm?: boolean;
  userIDRequestingPayoff?: number;
  status?: string;
  expiresAt?: string;
}

export type DealId = string;

export interface Experiment {
  id: string;
  variationId: string;
  assignedDateTime: string;
}

export interface DealCrmMeta {
  sourceId?: string | number;
  sourceName?: string;

  secondarySourceId?: string | number;
  secondarySourceName?: string;

  trackingCodeId?: string | number;
  trackingCodeName?: string;

  inShowroom?: boolean;
  isSold?: boolean;
  isInactive?: boolean;

  crmNoteHash?: string;
  forwardHash?: string;
  creditApplicationHash?: string;
  testDriveHash?: string;
  fastringHash?: string;
  testDriveAppointmentId?: string;
  pickupOrDeliveryAppointmentId?: string;
  // Rule of three - if you need to add any additional ShiftDigital data to crmMeta it should be broken out to somewhere else
  dealSentToShiftDateTime?: string | null;
  // ShiftDigital provided session ID for the deal
  shiftDigitalSessionId?: string;
  /**
   * These two fields are for new elead api integration
   * They will be used for getting customer info, opportunity info,
   * add/delete trade in, add/delete vehicle and other info for lead
   */
  customerId?: string;
  opportunityId?: string;
  salesPersonId?: string;
  // timestamp of last elead visit id sync
  lastEleadVisitIDSync?: string;
  uniteLeadId?: string;
  uniteLeadSentAt?: string;
  unitePartnerCode?: string;

  /** Keep track of in-showroom event creation in DGDG's Salesforce CRM. */
  lastDgdgInShowroomEventCreatedAt?: string;
  /** Keep track of in-showroom event creation. This will replace
   lastDgdgInShowroomEventCreatedAt once we replace
   the 4 usages of lastDgdgInShowroomEventCreatedAt in backend repo.
   TODO remove lastDgdgInShowroomEventCreatedAt after backend is updated */
  lastInShowroomEventCreatedAt?: string;
}

export interface Deal {
  id: DealId;
  createdById?: string | null;
  user_id?: number | null;
  dealer_id?: string | null;
  car_id?: string | null;
  online_car_id?: string | null;

  origin?: Origin | null;
  provider?: string | null;
  tradeIn?: number | null;
  amountOwed?: number | null;
  downPayment?: number | null;
  source?: SourceType | null;
  dealOwnerId?: string | null;
  crmVisitID?: string | null;
  DSEventID?: string | null;
  crmMeta?: DealCrmMeta | null;

  applicant?: Applicant | null;
  coapplicant?: Applicant | null;

  term?: number | null;
  apr?: number | null;
  selectedMileage?: number | null;
  selectedOffer?: Offer | FullOffer | OfferData | null;
  financeMeta?: {
    creditFirstSubmission?: string;
    dealertrackRefNumber?: string;
    routeOneSubmissionHash?: string;
    dmsDealInfo?: DMSDealInfo | null;
    routeOneId?: string;
    creditAppSentToUpstart?: boolean;
    creditAppSentToWestlake?: boolean;
    routeOneSelectedLenders?: string[];
    routeOneDealJacketId?: string;
  } | null;
  financeType?: FinanceType | null;

  progress?: string | null;
  attachedFiles?: {
    driverLicense?: string;
    driverLicenseBack?: string;
    altDriverLicense?: string;
    altDriverLicenseBack?: string;
    creditApplication?: string;
    creditApplicationSignature?: string;
    dealSummary?: string;
    dealSummarySignature?: string;
    documents?: DocumentScan[];
    applicantSpouseSignature?: string;
    coapplicantSpouseSignature?: string;
    pricingReview?: string;
  } | null;

  testDriveSelectedDateTime?: string | null;

  // Will use to differentiate if a testDrive is remote or not. If null, assume in-store
  testDriveDeliveryMethod?: TestDriveDeliveryMethod | null;
  // If remote, what address to deliver it to
  testDriveDeliveryAddress?: string | null;
  testDriveAppointmentComplete?: boolean;

  // pickupSelectedDateTime works for both pickup date and delivery date
  pickupSelectedDateTime?: string | null;
  deliveryDetails?: {
    address?: string;
    zip?: string;
  } | null;
  pickupDetails?: {
    address?: string;
    zip?: string;
  } | null;
  deliveryMethod?: string | null;
  pickupOrDeliveryAppointmentComplete?: boolean;

  upgrades?: {
    availableUpgrades?: { [index in FinanceType]?: DealUpgrade[] };
    selectedUpgrades?: DealUpgrade;
  } | null;
  discounts?: {
    availableDiscounts: {
      key?: string; // employee
      header?: string; // Do you qualify for an employee discount?
      description?: string;
      type?: string; // boolean, multiselect
    }[];
    selectedDiscounts: {
      disability?: boolean;
      employee?: boolean;
      military?: boolean;
      student?: boolean;
      trade?: string[];
      loyalty?: boolean;
      conquest?: boolean;
    };
    discountsValue?: number;
  } | null;
  tradeIns?: TradeIn[] | null;

  creditPull?: {
    token?: string;
    creditTier?: {
      tier: string;
      score: number;
      low: number;
      high: number;
    };
    note?: string;
    report?: string;
    forwarded?: boolean;
    // hash of the options used for the credit pull that token refers to
    optionsHash?: string;
    /**
     * @description The credit score if the pull was a hit.
     * `creditTier` will only be set when the dealer has a credit tier that the score
     * fits into. This will always be set regardless of what tiers the dealer is configured
     * with.
     */
    creditScore?: number;
    /**
     * @description The result code of the 700credit pull
     */
    resultCode?: string;
    /**
     * @description An error code for what went wrong if the 700credit pull outright failed.
     */
    errorCode?: 'GENERIC_SYSTEM_ERROR';
    /**
     * @description If resultCode or errorCode is set, this will be a human readable
     * description of the problem.
     */
    description?: string;
    /**
     * @description The time the credit pull was initially submitted to 700c
     */
    submittedAt?: Date;
  } | null;
  BYO?: {
    isLocked: boolean;
    carSelection?: {
      modelId?: string;
      trimId?: string;
      exteriorColorId?: string;
      interiorColorId?: string;
      modelCode?: string;
    };
    serializedConfigValue?: string;
  };
  crmNote?: string | null;
  forwardedToDealer?: string | null;
  creditSubmittedAt?: string | Date | null;
  testDriveStartedAt?: string | null;
  customerInfoSubmittedAt?: string | null;
  insuranceApplicationSubmittedAt?: Date | string | null;
  disableInsuranceTexting?: boolean | null;
  insurance?: {
    continueWithProviderUrl?: string;
  };
  startedAt?: string | null;
  coapplicantVisitID?: string | null;
  forwardedToCustomer?: string | null;
  turnOverStatus?: string | null;
  ownershipOptionsStatus?: string | null;
  result?: Result | null;
  creditRating?: CreditRating | null;
  carSource?: string | null;
  atcTaxesAndFees?: ATCTaxesAndFees | null;
  action?: ActionType | null;
  lastOpenedInStore?: string | Date | null;
  sentInHotleads?: boolean | null;
  crmSoldAt?: Date | string;
  priceOverrides?: PriceOverrides | null;
  dealSentToCustomer?: boolean | null;
  deposit_id?: string | null;
  // Same product id should not exist in selected bundle & selected product
  selectedBundleIds?: SelectedBundleId[] | null;
  selectedProducts?: SelectedProduct[] | null;
  selectedProgramOffers?: IncentivesProgramOffer[] | null;
  secondaryCarIds?: CarId[] | null;
  hasActiveChat?: boolean;
  usedChat?: boolean | null; // whether virtual showroom chat was ever used for this deal
  salesPersonName?: string | null;
  salesPerson?: string | null;
  onlineSource?: string | null;
  builder?: {
    overrideClientType?: string | null;
  };
  stickerSentToCustomer?: boolean | null;
  selectedAccessories?: SelectedAccessory[] | null;
  selectedFinanceResponseId?: FinanceResponseId | null;
  submittedToLendersAt?: Date | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  onePayLease?: boolean;
  // onePayLease rate + onePayLease markup when available
  onePayLeaseApr?: number | null;
  // Customized through pencil/first time calculating, effective rate if available
  onePaymentRate?: number | null;
  // Online steps that are disabled for the deal
  disabledSteps?:
    | {
        [index in OnlineStep]?: boolean | null;
      }
    | null;
  notifications?: {
    // specify schedule time as a unix timestamp,
    // for comparison as TEXT
    [index in DealNotificationType]: number;
  };
  sentDocumentsAt?: Date | string | null;
  financeResponseForwardedAt?: Date | string | null;
  envelopeId?: string | null;
  addInceptionFeesToTotalAtStart?: boolean;
  updatedInOnlineAt?: Date | string | null;
  creditRange?: CreditRange;
  hasChangedProtectionPlanInOnline?: boolean | null;
  hasChangedProtectionPlanInStore?: boolean | null;
  pencilId?: string;
  userHasChosenDealOwner?: boolean | null;
  userFirstName?: string;
  userLastName?: string;
  carName?: string;
  autodata?: {
    isStartedFromMobile?: boolean;
    urlUsedToStartOnlineExperience?: string;
    digitalAdvertising?: {
      gclid?: string; // Identify the campaign and other attributes of the click associated with the ad
      utm_source?: string; // The site that sent the traffic
      utm_medium?: string; // What was clicked to bring the user to the site
      utm_id?: string; // A unique identifier to track campaigns across channels and platforms
      utm_content?: string; // The type of link used, such as email or cost per click
      utm_campaign?: string; // A specific promotion or strategic campaign
      utm_term?: string; // A list of search terms, often separated by a plus sign
      ads_adgroup?: string; // Identifier for an ad group
      ads_adid?: string; // Identifier for an individual ad
      ads_digadprovider?: string; // Identier for the ad provider
      referrerDomain?: string; // The domain where the deal originated
      fullUrlFromParent?: string; // The full URL from the parent that started the IFrame
    };
  };
  isDigitalFinance?: boolean;
  experiments?: Experiment[] | null;
  car?: Car;
  financeResponse?: FinanceResponse;
  creditAppSentToCustomerAt?: Date;
  lockedAt?: Date | null;
  showMonthlyPaymentRange?: boolean;
  deskingLockedAt?: Date | null;
  deskingLockedBy?: number | null;
  showMultiDownPayments?: boolean;
}

export interface DealWithDmsAnalyticsData extends Deal {
  dmsVin?: string | null;
  salesType?: SalesType;
  dateCreated?: string | null;
  dmsType?: DMSType;
  dmsAnalyticsData?: Pick<
    DMSAnalyticsData,
    'vin' | 'dmsType' | 'salesType' | 'dateCreated'
  >[];
}

export type DealWithTradeInRecords = Omit<Deal, 'tradeIns'> & {
  tradeIns?: TradeInRecord[] | null;
};

export type PartialDeal = Partial<Deal>;

export function isFullDeal(deal?: PartialDeal): deal is Deal {
  return isDefined(deal) && isDefined(deal.id);
}

export enum DealNotificationType {
  ONLINE_DEAL_UPDATED = 'ONLINE_DEAL_UPDATED',
  FORWARDED_DEAL_UPDATED = 'FORDWARDED_DEAL_UPDATED',
  THIRD_PARTY_LEAD_TEXT = 'THIRD_PARTY_LEAD_TEXT',
  THIRD_PARTY_LEAD_EMAIL = 'THIRD_PARTY_LEAD_EMAIL',
}

export interface Lead {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  salesPerson?: string | null;
  visitID?: number | string | null;
  car?: null | {
    id?: string | null;
    name?: string | null;
    year?: string | number | null;
    make?: string | null;
    model?: string | null;
    trim?: string | null;
    status?: string | null;
    stock?: string | null;
  };
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormAddress {
  id?: number | null;
  zip?: string | null;
  address?: string | null;
  apt?: string | null;
  city?: string | null;
  state?: string | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormContactInfo {
  id?: number | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
  phoneNumber?: string | null;
  homePhoneNumber?: string | null;
  preferredContact?: string | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormApplicant {
  id?: number | null;
  dealId?: string | null;
  contactInfoId?: number | null;
  role?: 'applicant' | 'co-applicant' | null;
  ssn?: string | null;
  sin?: string | null;
  dateOfBirth?: string | null;
  driversLicenseNumber?: string | null;
  driversLicenseState?: string | null;
  comments?: string | null;
  maritalStatus?: MaritalStatus | null;
  hasCoApplicant?: boolean | null;
  coApplicantRelation?: CoapplicantRelation | null;
  isEmployed?: boolean | null;
  employmentStatus?: string | null;
  otherIncome?: number | null;
  otherIncomeSource?: string | null;
  otherIncomeSourceKey?: string | null;
  incomeType?: IncomeType | null;
  incomeSubType?: IncomeSubType | null;
  annualIncome?: number | null;
  additionalAnnualIncome?: number | null;
  hourlyIncomeRate?: number | null;
  hoursPerWeek?: number | null;
  otherIncomeType?: string | null;
  otherIncomeDescription?: string | null;
  otherIncomeAnnualAmount?: number | null;
  paycheckFrequency?: PaycheckFrequency | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  residencies?: FormResidency[] | null;
  employments?: FormEmployment[] | null;
  spouses?: FormSpouse[] | null;
  personalReferences?: FormPersonalReference[] | null;
  deal?: Deal | null;
  contactInfo?: FormContactInfo | null;
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormResidency {
  id?: number | null;
  applicantId?: number | null;
  addressId?: number | null;
  yearsAtAddress?: number | null;
  monthsAtAddress?: number | null;
  rentType?: RentType | null;
  mortgageLeaseAmount?: number | null;
  isCurrent?: boolean | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  applicant?: FormApplicant | null;
  address?: FormAddress | null;
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormEmployment {
  id?: number | null;
  applicantId?: number | null;
  companyAddressId?: number | null;
  companyName?: string | null;
  employerPhone?: string | null;
  jobTitle?: string | null;
  jobId?: number | null;
  yearsAtCompany?: number | null;
  monthsAtCompany?: number | null;
  companyStartDate?: string | null;
  monthlySalary?: number | null;
  isCurrent?: boolean | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  applicant?: FormApplicant | null;
  companyAddress?: FormAddress | null;
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormSpouse {
  id?: number | null;
  applicantId?: number | null;
  addressId?: number | null;
  contactInfoId?: number | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  applicant?: FormApplicant | null;
  address?: FormAddress | null;
  contactInfo?: FormContactInfo | null;
}

// This type is used by the Deal's Applicant Schema,
// which contains a relational representation of the applicant JSON.
export interface FormPersonalReference {
  id?: number | null;
  applicantId?: number | null;
  contactInfoId?: number | null;
  referenceType?: ReferenceType | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  applicant?: FormApplicant | null;
  contactInfo?: FormContactInfo | null;
}
